import {
  ElementType,
  FC,
  forwardRef,
  ComponentPropsWithRef,
  PropsWithChildren,
  ComponentPropsWithoutRef,
  ReactNode,
} from 'react';
import { css, cx } from '@linaria/atomic';
import composeRefs from '@seznam/compose-react-refs';
import { Text, useTooltip } from '../../';
import { IconProps } from '../../../icon';
import { PopoverMenuStyles } from './popover-menu.styles';

const notAllowedWrapperStyle = css`
  display: block;
  cursor: not-allowed;
  width: 100%;
`;

const widthFullStyle = css`
  width: 100%;
`;

export type ElementTypePartial = Extract<ElementType, 'a' | 'button' | 'span'>;

export type PopoverMenuItem = {
  active?: boolean;
  disabled?: boolean;
  Icon?: FC<React.PropsWithChildren<Pick<IconProps, 'size' | 'color'>>>;
  label?: string;
  hoverLabel?: ReactNode;
  menuId?: string;
  trackingId?: string;
  destructive?: boolean;
  darkMode?: boolean;
  preserveCasing?: boolean;
};

type MakeHrefRequired<C> = C extends Extract<ElementTypePartial, 'a'> ? { href: string } : Record<string, unknown>;
type PolymorphicRef<C extends ElementTypePartial> = ComponentPropsWithRef<C>['ref'];
type AsProp<C extends ElementTypePartial> = {
  as?: C;
};
type PropsToOmit<C extends ElementTypePartial, P> = keyof (AsProp<C> & P);
type PolymorphicComponentProp<C extends ElementTypePartial, Props = Record<string, unknown>> = PropsWithChildren<
  Props & AsProp<C>
> &
  Omit<ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>;
type PolymorphicComponentPropWithRef<
  C extends ElementTypePartial,
  Props = Record<string, unknown>
> = PolymorphicComponentProp<C, Props> & { ref?: PolymorphicRef<C> };
export type PopoverMenuItemProps<C extends ElementTypePartial> = PolymorphicComponentPropWithRef<C, PopoverMenuItem> &
  MakeHrefRequired<C>;
type PopoverComponent = <C extends ElementTypePartial = 'button'>(props: PopoverMenuItemProps<C>) => React.ReactNode;

// eslint-disable-next-line react/display-name
export const PopoverMenuItem: PopoverComponent = forwardRef(
  <C extends ElementTypePartial = 'button'>(
    {
      active,
      as,
      disabled,
      Icon,
      hoverLabel,
      children,
      menuId,
      destructive,
      trackingId,
      darkMode = false,
      preserveCasing = false,
      ...rest
    }: PopoverMenuItemProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const Component = as || ('button' as ElementType);
    const styles = PopoverMenuStyles.menuItem({ active, disabled, destructive, darkMode, preserveCasing });

    const { Tooltip, tooltipProps, triggerProps } = useTooltip({
      placement: 'bottom',
    });

    const content = (
      <>
        {Icon && <Icon size={16} color={destructive ? 'error' : 'default'} />}
        {typeof children === 'string' ? (
          <Text as='span' className={`menu-item-text__${menuId}`} color={destructive ? 'error' : 'default'}>
            {children}
          </Text>
        ) : (
          children
        )}
      </>
    );

    if (disabled && hoverLabel) {
      return (
        <span {...triggerProps} ref={triggerProps.ref} className={notAllowedWrapperStyle}>
          <Tooltip {...tooltipProps}>{hoverLabel}</Tooltip>
          <Component
            {...rest}
            disabled={disabled}
            className={cx(styles, widthFullStyle, rest.className)}
            role='menuitem'
            ref={ref}
            data-trackingid={trackingId}
          >
            {content}
          </Component>
        </span>
      );
    }

    return (
      <Component
        {...rest}
        {...triggerProps}
        ref={composeRefs(ref, triggerProps.ref)}
        data-trackingid={trackingId}
        disabled={disabled}
        className={cx(styles, widthFullStyle, rest.className)}
        role='menuitem'
      >
        {hoverLabel && <Tooltip {...tooltipProps}>{hoverLabel}</Tooltip>}
        {content}
      </Component>
    );
  }
);
